import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppSettingsService } from '../app-settings.service';
import { IRequestOptions } from '../http.service';
import { hd } from '..';
import { shareReplay } from 'rxjs/operators';

export interface IMCConnection {
  id: number;
  connectionName: string;
  accessUrl: string;
  clientId: string;
  updateClientSecret: boolean;
  clientSecret: string;
  updateRcCertificateData: boolean;
  rcCertificateData: string;
  updateRcKeyData: boolean;
  rcKeyData: string;
  grantType: string;
  issuer: string;
  aliases: string;
  isDefault: boolean;
}

export interface IMCConnectionInfo {
  id: number;
  connectionName: string;
  accessUrl: string;
  aliases: string;
  isDefault: boolean;
}

export enum ServerAuthenticationMode {
  windows = 0,
  database = 1,
}

export interface IMCAutogenerateConnection {
  id?: number;
  connectionName: string;
  accessUrl: string;
  serverName: string;
  databaseName: string;
  authenticationMode: ServerAuthenticationMode;
  userName: string;
  password: string;
}

export interface IDefaultConnectionName {
  connectionName: string;
}

@Injectable({
  providedIn: 'root',
})
export class McConnectionsService {
  mcConnection$: Observable<IMCConnectionInfo[]>;
  constructor(private http: HttpClient, private appSettings: AppSettingsService) {
    this.mcConnection$ = this.get().pipe(shareReplay(1));
  }

  /** Gets the avaiable MC Connections */
  public get = (excludeIds?: string[]): Observable<IMCConnectionInfo[]> => {
    const options: IRequestOptions = {
      params: {},
      headers: new HttpHeaders(),
    };

    if (excludeIds) {
      options.params['excludeIds'] = excludeIds.join(',');
    }

    return this.http.get<IMCConnectionInfo[]>(this.appSettings.accountServiceUrl + 'connections/info', options);
  };

  /** Gets the avaiable MC Connections */
  public getById = (mcId: number): Observable<IMCConnection> => {
    return this.http.get<IMCConnection>(this.appSettings.accountServiceUrl + `connections/${mcId}`);
  };

  /** Add a new MC Connection */
  public add = (value: IMCConnection): Observable<IMCConnection> => {
    return this.http.post<IMCConnection>(this.appSettings.accountServiceUrl + 'connections', value);
  };

  /** Add a new MC Connection */
  public addAutogenerate = (value: IMCAutogenerateConnection): Observable<IMCConnection> => {
    return this.http.post<IMCConnection>(this.appSettings.accountServiceUrl + 'connections/autogenerate', value);
  };

  /** Add a new MC Connection */
  public updateAutogenerate = (value: IMCAutogenerateConnection): Observable<IMCConnection> => {
    return this.http.put<IMCConnection>(
      this.appSettings.accountServiceUrl + `connections/autogenerate/${value.id}`,
      value
    );
  };

  /** Delete the MC Connection */
  public delete = (value: IMCConnection): Observable<IMCConnection> => {
    return this.http.delete<IMCConnection>(this.appSettings.accountServiceUrl + `connections/${value.id}`);
  };

  /** Update the MC Connection */
  public update = (value: IMCConnection): Observable<IMCConnection> => {
    return this.http.put<IMCConnection>(this.appSettings.accountServiceUrl + `connections/${value.id}`, value);
  };

  /** Gets the default MC Connection name */
  public getSotiAssistMcName = (mcConnectionId: number = 0): Observable<hd.IDefaultMcInstance> => {
    return this.http.get<hd.IDefaultMcInstance>(
      this.appSettings.accountServiceUrl + `connections/${mcConnectionId}/connectionName`
    );
  };

  public updateMcName = (
    defaultInstance: hd.IDefaultMcInstance,
    mcConnectionId: number = 0
  ): Observable<hd.IDefaultMcInstance> => {
    return this.http.put<hd.IDefaultMcInstance>(
      this.appSettings.accountServiceUrl + `connections/${mcConnectionId}/connectionName`,
      defaultInstance
    );
  };
}
